import { couplingFitsHitch, type RigListItem, type RigTrailer, type RigTruck } from '@chukua/common';

/** The vehicle the drawer couples something to: a truck being given a trailer, or a trailer being put on a truck. */
export type CoupleSubject =
  | { kind: 'truck'; truck: Pick<RigTruck, 'id' | 'registration' | 'coupling'>; currentTrailerId: string | null; currentTrailerRegistration: string | null }
  | { kind: 'trailer'; trailer: Pick<RigTrailer, 'id' | 'registration' | 'hitch'>; currentTruckId: string | null };

export type CoupleGroup = 'parked' | 'other_truck' | 'no_fit';

export interface CoupleOption {
  /** The picked vehicle's id. */
  value: string;
  label: string;
  group: CoupleGroup;
  disabled: boolean;
  /** What the option's second line says. */
  note: string;
  rig: RigListItem;
}

export const COUPLE_GROUP_LABELS: Record<CoupleGroup, string> = { parked: 'Free', other_truck: 'On another truck', no_fit: "Doesn't fit" };

function fits(truck: Pick<RigTruck, 'coupling'> | null, trailer: Pick<RigTrailer, 'hitch'> | null): boolean {
  return Boolean(truck?.coupling && trailer?.hitch && couplingFitsHitch(truck.coupling, trailer.hitch));
}

function needsWhat(trailer: Pick<RigTrailer, 'hitch'>): string {
  return trailer.hitch === 'drawbar' ? 'Drawbar trailer: needs a drawbar hitch' : 'Semi-trailer: needs a fifth wheel';
}

function trailerOption(subject: Extract<CoupleSubject, { kind: 'truck' }>, rig: RigListItem): CoupleOption | null {
  const { trailer, truck } = rig;
  if (!trailer || trailer.id === subject.currentTrailerId) return null;
  const base = { value: trailer.id, label: trailer.registration, rig };
  if (!fits(subject.truck, trailer)) return { ...base, group: 'no_fit', disabled: true, note: needsWhat(trailer) };
  if (truck) return { ...base, group: 'other_truck', disabled: false, note: `On ${truck.registration}, which loses it` };
  return { ...base, group: 'parked', disabled: false, note: rig.parkedAt ? `Parked at ${rig.parkedAt.zoneName ?? rig.parkedAt.zoneCode}` : 'Never coupled' };
}

function truckOption(subject: Extract<CoupleSubject, { kind: 'trailer' }>, rig: RigListItem): CoupleOption | null {
  const { truck, trailer } = rig;
  if (!truck || truck.id === subject.currentTruckId) return null;
  const base = { value: truck.id, label: truck.registration, rig };
  if (!fits(truck, subject.trailer)) {
    return {
      ...base,
      group: 'no_fit',
      disabled: true,
      note: truck.coupling === 'none' ? "Doesn't tow a trailer" : `Has no ${subject.trailer.hitch === 'drawbar' ? 'drawbar hitch' : 'fifth wheel'}`,
    };
  }
  if (trailer) return { ...base, group: 'other_truck', disabled: false, note: `Pulling ${trailer.registration}, which is dropped` };
  return { ...base, group: 'parked', disabled: false, note: 'No trailer' };
}

const GROUP_ORDER: CoupleGroup[] = ['parked', 'other_truck', 'no_fit'];

/**
 * The couple picker's choices from a page of rigs (gitaumoses4/chukua#390): what fits first, then what has to come off another
 * truck, then what doesn't fit, shown greyed with the reason rather than hidden, so an empty fifth wheel never looks like "no
 * trailers". The vehicle's own current partner is left out.
 */
export function coupleOptions(subject: CoupleSubject, rigs: RigListItem[]): CoupleOption[] {
  const options = rigs
    .map((rig) => (subject.kind === 'truck' ? trailerOption(subject, rig) : truckOption(subject, rig)))
    .filter((option): option is CoupleOption => option !== null);
  return GROUP_ORDER.flatMap((group) => options.filter((option) => option.group === group));
}

/** Giving a rig's truck a trailer, or swapping the one it pulls. */
export function truckSubject(rig: RigListItem): CoupleSubject | null {
  if (!rig.truck) return null;
  return { kind: 'truck', truck: rig.truck, currentTrailerId: rig.trailer?.id ?? null, currentTrailerRegistration: rig.trailer?.registration ?? null };
}

/** Putting a rig's trailer on a truck. */
export function trailerSubject(rig: RigListItem): CoupleSubject | null {
  if (!rig.trailer) return null;
  return { kind: 'trailer', trailer: rig.trailer, currentTruckId: rig.truck?.id ?? null };
}

export interface CoupleDrawerLabels {
  title: string;
  submit: string;
  picker: string;
  placeholder: string;
  /** The drop-place question, when a trailer comes off a truck here. */
  dropQuestion: string;
  /** When the picked trailer is on another truck: which one loses it. */
  takenFrom: string | null;
}

/** What the couple drawer says, for a truck being given a trailer or a trailer being put on a truck. */
export function coupleDrawerLabels(subject: CoupleSubject, picked: CoupleOption | undefined): CoupleDrawerLabels {
  if (subject.kind === 'truck') {
    const swapping = subject.currentTrailerId !== null;
    const otherTruck = picked?.rig.truck;
    return {
      title: `${swapping ? 'Swap trailer on' : 'Couple a trailer to'} ${subject.truck.registration}`,
      submit: swapping ? 'Swap trailer' : 'Couple trailer',
      picker: 'Trailer',
      placeholder: 'Select or search trailers by registration',
      dropQuestion: `Where is ${subject.currentTrailerRegistration} dropped?`,
      takenFrom: otherTruck ? `${picked.label} is on ${otherTruck.registration}. Coupling it here takes it off ${otherTruck.registration}.` : null,
    };
  }
  return {
    title: `Couple ${subject.trailer.registration} to a truck`,
    submit: 'Couple trailer',
    picker: 'Truck',
    placeholder: 'Select or search trucks by registration',
    dropQuestion: `Where is ${picked?.rig.trailer?.registration ?? 'its trailer'} dropped?`,
    takenFrom: null,
  };
}
